import { useEffect } from "react";

function updateTag(selector, attribute, value, createTagFn) {
  if (!value) return;
  let element = document.querySelector(selector);
  if (!element && createTagFn) {
    element = createTagFn();
    document.head.appendChild(element);
  }
  if (element) {
    element[attribute] = value;
  }
}

/**
 * Custom hook to declaratively and reliably update page SEO tags:
 * - Document title
 * - Canonical link
 * - Meta description
 * - OpenGraph tags (title, description, url, image)
 * - Twitter card tags (title, description, image)
 * 
 * Guarantees that tags are safely updated without leaking elements or causing UI re-render flashes.
 */
export function useSeo({
  title,
  description,
  canonical,
  image,
  type = "website",
} = {}) {
  useEffect(() => {
    if (title) {
      document.title = title;
      updateTag('meta[property="og:title"]', "content", title, () => {
        const el = document.createElement("meta");
        el.setAttribute("property", "og:title");
        return el;
      });
      updateTag('meta[name="twitter:title"]', "content", title, () => {
        const el = document.createElement("meta");
        el.name = "twitter:title";
        return el;
      });
    }

    if (description) {
      updateTag('meta[name="description"]', "content", description, () => {
        const el = document.createElement("meta");
        el.name = "description";
        return el;
      });
      updateTag('meta[property="og:description"]', "content", description, () => {
        const el = document.createElement("meta");
        el.setAttribute("property", "og:description");
        return el;
      });
      updateTag('meta[name="twitter:description"]', "content", description, () => {
        const el = document.createElement("meta");
        el.name = "twitter:description";
        return el;
      });
    }

    if (canonical) {
      updateTag('link[rel="canonical"]', "href", canonical, () => {
        const el = document.createElement("link");
        el.rel = "canonical";
        return el;
      });
      updateTag('meta[property="og:url"]', "content", canonical, () => {
        const el = document.createElement("meta");
        el.setAttribute("property", "og:url");
        return el;
      });
      updateTag('meta[name="twitter:url"]', "content", canonical, () => {
        const el = document.createElement("meta");
        el.name = "twitter:url";
        return el;
      });
    }

    if (image) {
      updateTag('meta[property="og:image"]', "content", image, () => {
        const el = document.createElement("meta");
        el.setAttribute("property", "og:image");
        return el;
      });
      updateTag('meta[name="twitter:image"]', "content", image, () => {
        const el = document.createElement("meta");
        el.name = "twitter:image";
        return el;
      });
    }

    if (type) {
      updateTag('meta[property="og:type"]', "content", type, () => {
        const el = document.createElement("meta");
        el.setAttribute("property", "og:type");
        return el;
      });
    }
  }, [title, description, canonical, image, type]);
}

export default useSeo;
